import { CurrencySelect } from 'components/CurrencySelect'
import { CommonBasesType } from 'components/SearchModal/types'

import { Currency, NATIVE, WNATIVE } from '@pancakeswap/sdk'
import {
  FlexGap,
  AutoColumn,
  CardBody,
  Card,
  AddIcon,
  PreTitle,
  DynamicSection,
  RefreshIcon,
  IconButton,
  Box,
  Button,
  SyncAltIcon,
} from '@pancakeswap/uikit'

import { FeeAmount } from '@pancakeswap/v3-sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'

import currencyId from 'utils/currencyId'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from '@pancakeswap/localization'

import Page from 'views/Page'
import { AppHeader } from 'components/App'
import EBox from 'components/EBox'
import { styled } from 'styled-components'
import { atom } from 'jotai'

import { useCurrency } from 'hooks/Tokens'
import useStableConfig, { StableConfigContext } from 'views/Swap/hooks/useStableConfig'
import AddStableLiquidity from 'views/AddLiquidity/AddStableLiquidity'
import AddFSNftLiquidity from 'views/AddLiquidity/AddFSNftLiquidity'
import AddLiquidity from 'views/AddLiquidity'
import noop from 'lodash/noop'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useAddLiquidityV2FormDispatch } from 'state/mint/reducer'
import { resetMintState } from 'state/mint/actions'
import { safeGetAddress } from 'utils'
import FeeSelector from './formViews/V3FormView/components/FeeSelector'
import V3FormView from './formViews/V3FormView'
import { HandleFeePoolSelectFn, SELECTOR_TYPE } from './types'
import StableFormView from './formViews/StableFormView'
import V2FormView from './formViews/V2FormView'
import { useCurrencyParams } from './hooks/useCurrencyParams'
import FSNftFormView from './formViews/FSNftFormView'

export const BodyWrapper = styled(EBox)`
  width: 100%;
  z-index: 1;
  margin-bottom: 24px;
  border-radius: 25px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => theme.colors.backgroundAlt};
  box-shadow: 0 16px 40px -16px rgba(0, 0, 0, 0.6);
`

/* two-column layout where DepositAmount is moved at the very end on mobile. */
export const ResponsiveTwoColumns = styled.div`
  display: grid;
  grid-column-gap: 32px;
  grid-row-gap: 16px;
  grid-template-columns: 1fr;

  grid-template-rows: max-content;
  grid-auto-flow: row;

  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: 1fr 1fr;
  }
`

const InvertButton = styled(Button)`
  padding: 0;
  aspect-ratio: 1;
  border: none;
`

const selectTypeAtom = atom(SELECTOR_TYPE.V3)

interface UniversalAddLiquidityPropsType {
  currencyIdA: string
  currencyIdB: string
  isV2?: boolean
  preferredSelectType?: SELECTOR_TYPE
  preferredFeeAmount?: FeeAmount
  selectorType: SELECTOR_TYPE
  liquidityUSD?: number
  currentPrice?: number
  token0Percentage?: number
  token1Percentage?: number
  fixed?: boolean
  onDismiss?: () => void
}

export function UniversalAddLiquidity({
  currencyIdA,
  currencyIdB,
  preferredFeeAmount,
  selectorType,
  liquidityUSD,
  currentPrice,
  token0Percentage,
  token1Percentage,
  fixed = false,
  onDismiss,
}: UniversalAddLiquidityPropsType) {
  const { chainId } = useActiveChainId()
  const { t } = useTranslation()

  const [invertCurrency, setInvertCurrency] = useState(false)

  const dispatch = useAddLiquidityV2FormDispatch()

  useEffect(() => {
    if (!currencyIdA && !currencyIdB) {
      dispatch(resetMintState())
    }
  }, [dispatch, currencyIdA, currencyIdB])

  const router = useRouter()
  const baseCurrency = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)

  const stableConfig = useStableConfig({
    tokenA: baseCurrency,
    tokenB: currencyB,
  })

  const quoteCurrency =
    baseCurrency && currencyB && baseCurrency.wrapped.equals(currencyB.wrapped) ? undefined : currencyB

  const [, , feeAmountFromUrl] = router.query.currency || []

  // fee selection from url
  const feeAmount: FeeAmount | undefined = useMemo(() => {
    return (
      preferredFeeAmount ||
      (feeAmountFromUrl && Object.values(FeeAmount).includes(parseFloat(feeAmountFromUrl))
        ? parseFloat(feeAmountFromUrl)
        : undefined)
    )
  }, [preferredFeeAmount, feeAmountFromUrl])

  const handleCurrencySelect = useCallback(
    (currencyNew: Currency, currencyIdOther?: string): (string | undefined)[] => {
      const currencyIdNew = currencyId(currencyNew)

      if (currencyIdNew === currencyIdOther) {
        // not ideal, but for now clobber the other if the currency ids are equal
        return [currencyIdNew, undefined]
      }
      // prevent wnative + native
      const isNATIVEOrWNATIVENew =
        currencyNew?.isNative || (chainId !== undefined && currencyIdNew === WNATIVE[chainId]?.address)
      const isNATIVEOrWNATIVEOther =
        currencyIdOther !== undefined &&
        (currencyIdOther === NATIVE[chainId]?.symbol ||
          (chainId !== undefined && safeGetAddress(currencyIdOther) === WNATIVE[chainId]?.address))

      if (isNATIVEOrWNATIVENew && isNATIVEOrWNATIVEOther) {
        return [currencyIdNew, undefined]
      }

      return [currencyIdNew, currencyIdOther]
    },
    [chainId],
  )

  const handleCurrencyASelect = useCallback(
    (currencyANew: Currency) => {
      const [idA, idB] = handleCurrencySelect(currencyANew, currencyIdB)
      if (idB === undefined) {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              currency: [idA],
            },
          },
          undefined,
          { shallow: true },
        )
      } else {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              currency: [idA, idB],
            },
          },
          undefined,
          { shallow: true },
        )
      }
    },
    [handleCurrencySelect, currencyIdB, router],
  )

  const handleCurrencyBSelect = useCallback(
    (currencyBNew: Currency) => {
      const [idB, idA] = handleCurrencySelect(currencyBNew, currencyIdA)
      if (idA === undefined) {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              currency: [idB],
            },
          },
          undefined,
          { shallow: true },
        )
      } else {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              currency: [idA, idB],
            },
          },
          undefined,
          { shallow: true },
        )
      }
    },
    [handleCurrencySelect, currencyIdA, router],
  )

  const handleFeePoolSelect = useCallback<HandleFeePoolSelectFn>(
    ({ type, feeAmount: newFeeAmount }) => {
      if (newFeeAmount) {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              currency: [currencyIdA, currencyIdB, newFeeAmount.toString()],
            },
          },
          undefined,
          { shallow: true },
        )
      } else {
        router.replace(
          {
            pathname: router.pathname.replace('/v2', ''),
            query: {
              ...router.query,
              currency: [currencyIdA, currencyIdB],
            },
          },
          undefined,
          { shallow: true },
        )
      }
    },
    [currencyIdA, currencyIdB, router],
  )

  const handleSelectV2 = useCallback(() => {
    router.replace(
      {
        pathname: router.pathname,
        query: router.query,
      },
      `/v2/add/${currencyIdA}/${currencyIdB}`,
      { shallow: true },
    )
  }, [currencyIdA, currencyIdB, router])

  useEffect(() => {
    if (preferredFeeAmount && !feeAmountFromUrl && selectorType === SELECTOR_TYPE.V3) {
      handleFeePoolSelect({ type: selectorType, feeAmount: preferredFeeAmount })
    }
  }, [preferredFeeAmount, feeAmountFromUrl, handleFeePoolSelect, selectorType])

  return (
    <Box mt="8px">
      {selectorType === SELECTOR_TYPE.V3 ? (
        <V3FormView
          invertCurrency={invertCurrency}
          feeAmount={feeAmount}
          baseCurrency={baseCurrency}
          quoteCurrency={quoteCurrency}
          currencyIdA={currencyIdA}
          currencyIdB={currencyIdB}
          liquidityUSD={liquidityUSD}
          currentPrice={currentPrice}
          token0Percentage={token0Percentage}
          token1Percentage={token1Percentage}
        />
      ) : selectorType === SELECTOR_TYPE.STABLE ? (
        <StableConfigContext.Provider value={stableConfig}>
          <AddStableLiquidity currencyA={baseCurrency} currencyB={quoteCurrency}>
            {(props) => (
              <>
                { 
                  !fixed &&
                  <AutoColumn gap="8px" mb="24px">
                    <PreTitle mb="8px">{t('Choose Token Pair')}</PreTitle>
                    <MobileFlexGap gap="8px" width="100%" alignItems="center">
                      <FlexBox>
                        <CurrencySelect
                          id="add-liquidity-select-tokena"
                          selectedCurrency={invertCurrency ? quoteCurrency : baseCurrency}
                          onCurrencySelect={invertCurrency ? handleCurrencyBSelect : handleCurrencyASelect}
                          showCommonBases
                          commonBasesType={CommonBasesType.LIQUIDITY}
                          hideBalance
                          showOnlyStableCoins={true}
                          width="100%"
                        />
                      </FlexBox>
                      <InvertButton variant="tertiary" scale="sm" onClick={() => setInvertCurrency(!invertCurrency)}>
                        <SyncAltIcon />
                      </InvertButton>
                      <FlexBox>
                        <CurrencySelect
                          id="add-liquidity-select-tokenb"
                          selectedCurrency={invertCurrency ? baseCurrency : quoteCurrency}
                          onCurrencySelect={invertCurrency ? handleCurrencyASelect : handleCurrencyBSelect}
                          showCommonBases
                          commonBasesType={CommonBasesType.LIQUIDITY}
                          hideBalance
                          showOnlyStableCoins={true}
                          width="100%"
                        />
                      </FlexBox>
                    </MobileFlexGap>
                  </AutoColumn>
                }
                <StableFormView {...props} stableLpFee={stableConfig?.stableSwapConfig?.stableLpFee} />
              </>
            )}
          </AddStableLiquidity>
        </StableConfigContext.Provider>
      ) : selectorType === SELECTOR_TYPE.V2 ? (
        <AddLiquidity currencyA={baseCurrency} currencyB={quoteCurrency}>
          {(props) => <V2FormView {...props} onDismiss={onDismiss} handleCurrencySelect={handleCurrencySelect} router={router} />}
        </AddLiquidity>
      ) : (
        <ResponsiveTwoColumns>
          <AutoColumn alignSelf="start" mt="8px">
            <PreTitle mb="8px">{t('Choose Token Pair')}</PreTitle>
            <FlexGap justifyContent="space-between" width="100%" mb="8px" alignItems="center" flexWrap="wrap">
              <CurrencySelect
                id="add-liquidity-select-tokena"
                selectedCurrency={invertCurrency ? quoteCurrency : baseCurrency}
                onCurrencySelect={invertCurrency ? handleCurrencyBSelect : handleCurrencyASelect}
                showCommonBases
                commonBasesType={CommonBasesType.LIQUIDITY}
                hideBalance
              />
              <SwapButtonContainer>
                <InvertButton variant="tertiary" scale="sm" onClick={() => setInvertCurrency(!invertCurrency)}>
                  <SyncAltIcon />
                </InvertButton>
              </SwapButtonContainer>
              <CurrencySelect
                id="add-liquidity-select-tokenb"
                selectedCurrency={invertCurrency ? baseCurrency : quoteCurrency}
                onCurrencySelect={invertCurrency ? handleCurrencyASelect : handleCurrencyBSelect}
                showCommonBases
                commonBasesType={CommonBasesType.LIQUIDITY}
                hideBalance
              />
            </FlexGap>
          </AutoColumn>
        </ResponsiveTwoColumns>
      )}
    </Box>
  )
}

const SELECTOR_TYPE_T = {
  [SELECTOR_TYPE.STABLE]: <Trans>Add Stable Liquidity</Trans>,
  [SELECTOR_TYPE.V2]: <Trans>Add V2 Liquidity</Trans>,
  [SELECTOR_TYPE.V3]: <Trans>Add V3 Liquidity</Trans>,
  // [SELECTOR_TYPE.FSNFT]: <Trans>Add fsNFT Liquidity</Trans>,
} as const satisfies Record<SELECTOR_TYPE, JSX.Element>

export function AddLiquidityV3Layout({
  showRefreshButton = false,
  handleRefresh,
  selectorType,
  children,
}: {
  showRefreshButton?: boolean
  handleRefresh?: () => void
  selectorType: SELECTOR_TYPE
  children: React.ReactNode
}) {
  const { t } = useTranslation()

  const { currencyIdA, currencyIdB, feeAmount } = useCurrencyParams()

  const baseCurrency = useCurrency(currencyIdA)
  const quoteCurrency = useCurrency(currencyIdB)

  const title = SELECTOR_TYPE_T[selectorType] || t('Add Liquidity')

  return (
    <Page>
      <BodyWrapper>
        <AppHeader
          title={title}
          subtitle={selectorType === SELECTOR_TYPE.STABLE ? t('Earn yield on your stablecoins by providing stable liquidity!|Users benefit from swapping stablecoins with minimal slippage and fees!') : undefined}
          IconSlot={
            <>
              {showRefreshButton && (
                <IconButton variant="text" scale="sm">
                  <RefreshIcon onClick={handleRefresh || noop} color="textSubtle" height={24} width={24} />
                </IconButton>
              )}
            </>
          }
        />
        {children}
      </BodyWrapper>
    </Page>
  )
}

const SwapButtonContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const FlexBox = styled(Box)`
  flex: 1;
  @media screen and (max-width: 480px) {
    width: 100%;
  }
`

const MobileFlexGap = styled(FlexGap)`
  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    
    ${InvertButton} {
      margin: 8px 0;
    }
  }
`
